import { Injectable } from '@angular/core';

/**
 * Mock Data Service
 * Provides mock data for all API endpoints when mockMode is enabled in environment
 */
@Injectable({
  providedIn: 'root'
})
export class MockDataService {

  constructor() { }

  /**
   * Mock User Info
   */
  getUserInfo() {
    return {
      data: {
        userId: 'mock-user-123',
        displayName: 'Mock User',
        email: 'mockuser@test.com',
        jobTitle: 'Medical Screener',
        department: 'Patient Services',
        officeLocation: 'Main Campus',
        mobilePhone: '555-1234',
        businessPhones: ['555-5678'],
        preferredLanguage: 'en-US'
      }
    };
  }

  /**
   * Mock User Avatar (base64 image data)
   */
  getUserAvatar() {
    // Returns a small placeholder avatar as base64
    return {
      data: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiM0Mjg1RjQiLz4KPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IndoaXRlIiBmb250LWZhbWlseT0iQXJpYWwiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5NVTwvdGV4dD4KPC9zdmc+'
    };
  }

  /**
   * Mock Microsoft Graph User Profile
   */
  getGraphUserProfile() {
    return {
      '@odata.context': 'https://graph.microsoft.com/v1.0/$metadata#users/$entity',
      id: 'mock-graph-user-id',
      businessPhones: ['555-5678'],
      displayName: 'Mock User',
      givenName: 'Mock',
      jobTitle: 'Medical Screener',
      mail: 'mockuser@test.com',
      mobilePhone: '555-1234',
      officeLocation: 'Main Campus',
      preferredLanguage: 'en-US',
      surname: 'User',
      userPrincipalName: 'mockuser@test.com'
    };
  }

  /**
   * Mock Microsoft Graph User Groups (for permissions)
   */
  getGraphUserGroups() {
    return {
      '@odata.context': 'https://graph.microsoft.com/v1.0/$metadata#directoryObjects',
      value: [
        {
          '@odata.type': '#microsoft.graph.group',
          id: 'SCREENER_GROUP_ID',
          displayName: 'MNS Screeners',
          description: 'Users who can perform medical necessity screenings'
        },
        {
          '@odata.type': '#microsoft.graph.group',
          id: 'ABN_VIEWER_GROUP_ID',
          displayName: 'ABN Viewers',
          description: 'Users who can view and generate ABN forms'
        },
        {
          '@odata.type': '#microsoft.graph.group',
          id: 'REPORTS_VIEWER_GROUP_ID',
          displayName: 'Reports Viewers',
          description: 'Users who can view reports'
        },
        {
          '@odata.type': '#microsoft.graph.group',
          id: 'ADMIN_GROUP_ID',
          displayName: 'MNS Administrators',
          description: 'Full administrative access'
        }
      ]
    };
  }

  /**
   * Mock Downtime Status
   */
  getDowntime() {
    return {
      data: {
        isDown: false,
        downtimeMessage: '',
        scheduledMaintenance: null
      }
    };
  }

  /**
   * Mock Online Help Content
   * Returns help content based on the screen ID
   */
  getOnlineHelp(screenId: string = 'screening') {
    const helpContent: { [key: string]: any } = {
      'screening': {
        screenId: 'screening',
        title: 'Medical Necessity Screening Help',
        content: `
          <h2>How to Use the Screening Module</h2>
          <p>The screening module helps you determine if medical tests meet Medicare/Medicaid coverage criteria before ordering them.</p>

          <h3>Step-by-Step Instructions</h3>
          <ol>
            <li><strong>Patient Information:</strong> Enter the patient's account number, name, date of birth, insurance type, and insurance number.</li>
            <li><strong>CPT Codes:</strong> Enter all procedure codes (CPT codes) you want to screen, separated by commas (e.g., 80053, 82947, 85025).</li>
            <li><strong>Diagnosis Codes:</strong> Enter all relevant ICD-10 diagnosis codes for the patient, separated by commas (e.g., E11.9, Z79.4, E11.65).</li>
            <li><strong>Submit:</strong> Click "Screen Medical Necessity" to check if the tests meet coverage criteria.</li>
          </ol>

          <h3>Tips</h3>
          <ul>
            <li>Always screen tests before creating orders in your EMR system</li>
            <li>Multiple tests can be screened simultaneously</li>
            <li>All screenings are logged for compliance purposes</li>
            <li>ABN forms will auto-generate for failed screenings</li>
          </ul>
        `,
        lastUpdated: new Date().toISOString()
      },
      'results': {
        screenId: 'results',
        title: 'Screening Results Help',
        content: `
          <h2>Understanding Your Screening Results</h2>
          <p>This page displays the results of your medical necessity screening and provides next steps.</p>

          <h3>Result Indicators</h3>
          <ul>
            <li><strong class="text-green-600">PASSED:</strong> The test meets LCD/NCD criteria and can be ordered without an ABN.</li>
            <li><strong class="text-red-600">FAILED:</strong> The test does not meet coverage criteria and requires an Advance Beneficiary Notice (ABN).</li>
          </ul>

          <h3>Next Steps</h3>
          <p><strong>For Passed Tests:</strong></p>
          <ul>
            <li>Click "Proceed to Order" to continue with the test order in your EMR</li>
            <li>No ABN is required</li>
          </ul>

          <p><strong>For Failed Tests:</strong></p>
          <ul>
            <li>Click "Generate ABN" to create an official CMS Advance Beneficiary Notice</li>
            <li>Have the patient sign the ABN before providing the service</li>
            <li>You may also choose to "Cancel Test" if the patient declines</li>
          </ul>

          <h3>Actions</h3>
          <ul>
            <li><strong>Print Summary:</strong> Print a copy of the screening results for your records</li>
            <li><strong>Export to Meditech:</strong> Send screening data to your EMR system</li>
            <li><strong>Save Results to History:</strong> Archive this screening for compliance reporting</li>
          </ul>
        `,
        lastUpdated: new Date().toISOString()
      },
      'abn-preview': {
        screenId: 'abn-preview',
        title: 'ABN Form Generator Help',
        content: `
          <h2>Advance Beneficiary Notice (ABN) Forms</h2>
          <p>This tool generates official CMS-R-131 ABN forms for tests that failed medical necessity screening.</p>

          <h3>Form Options</h3>
          <ul>
            <li><strong>Language:</strong> Choose English or Spanish based on patient preference</li>
            <li><strong>Print Size:</strong> Standard or Large Print (for accessibility)</li>
          </ul>

          <h3>How to Use the ABN</h3>
          <ol>
            <li>Review the auto-generated form for accuracy</li>
            <li>Click "Download" to save as PDF or "Print" for immediate printing</li>
            <li>Present the form to the patient and explain why Medicare may not pay</li>
            <li>Have the patient make their choice (Options 1, 2, or 3) and sign</li>
            <li>Keep the original signed form and provide a copy to the patient</li>
          </ol>

          <h3>Important Notes</h3>
          <ul>
            <li>The ABN must be obtained BEFORE providing the service</li>
            <li>This form is effective until January 31, 2026</li>
            <li>The patient signature is required - do not proceed without it</li>
            <li>Document the ABN in the patient's medical record</li>
          </ul>
        `,
        lastUpdated: new Date().toISOString()
      },
      'administration': {
        screenId: 'administration',
        title: 'System Administration Help',
        content: `
          <h2>Administration Module</h2>
          <p>Manage system settings, LCD/NCD files, facilities, and users.</p>

          <h3>File Management</h3>
          <p>Upload and manage LCD (Local Coverage Determination) and NCD (National Coverage Determination) files.</p>
          <ul>
            <li><strong>3M Solventum Files:</strong> Encoder data files from 3M</li>
            <li><strong>LCD/NCD Rules:</strong> Regional coverage determination rules</li>
            <li><strong>Charge Master Data:</strong> Facility pricing information</li>
          </ul>

          <h3>Regional Configuration</h3>
          <p>Configure facility settings and assign LCD/NCD rulesets:</p>
          <ul>
            <li>Select the active facility from the dropdown</li>
            <li>Choose the appropriate LCD/NCD ruleset for your region</li>
            <li>Review the current configuration details before saving</li>
          </ul>

          <h3>Facility Details</h3>
          <p>Update facility information used on ABN forms:</p>
          <ul>
            <li>Facility name, address, and contact information</li>
            <li>This information appears on all generated ABN forms</li>
          </ul>

          <h3>User Management</h3>
          <p>Add, edit, or remove system users:</p>
          <ul>
            <li><strong>Administrators:</strong> Full system access, can manage all settings</li>
            <li><strong>Managers:</strong> Can view reports and manage screenings</li>
            <li><strong>Schedulers:</strong> Can perform screenings and generate ABNs</li>
          </ul>
        `,
        lastUpdated: new Date().toISOString()
      },
      'reports': {
        screenId: 'reports',
        title: 'Analytics & Reports Help',
        content: `
          <h2>Analytics & Reports</h2>
          <p>View screening metrics, trends, and generate compliance reports.</p>

          <h3>Summary Statistics</h3>
          <ul>
            <li><strong>Total Screenings:</strong> Number of screenings performed in the last 30 days</li>
            <li><strong>Pass Rate:</strong> Percentage of tests that met medical necessity criteria</li>
            <li><strong>ABNs Generated:</strong> Count of Advance Beneficiary Notices created</li>
            <li><strong>Denied Claims Prevented:</strong> Estimated dollar amount saved by screening before ordering</li>
          </ul>

          <h3>Charts & Visualizations</h3>
          <p><strong>Screening Volume Trend:</strong> Daily screening activity over time</p>
          <p><strong>Pass vs Fail Distribution:</strong> Visual breakdown of screening outcomes</p>
          <p><strong>Top Failed Tests:</strong> CPT codes that most frequently fail medical necessity</p>

          <h3>Export Reports</h3>
          <p>Generate custom reports for compliance and analysis:</p>
          <ol>
            <li>Select a report type (Screening Summary, Failed Tests, ABN Log, etc.)</li>
            <li>Choose a date range (Last 7 days, 30 days, 90 days, or custom)</li>
            <li>Select export format (Excel, PDF, or CSV)</li>
            <li>Click "Generate Report" to download</li>
          </ol>

          <h3>Quick Insights</h3>
          <p>Key findings and action items:</p>
          <ul>
            <li><strong>Peak Hours:</strong> When screenings are most frequent</li>
            <li><strong>Top Performer:</strong> User with highest accuracy rate</li>
            <li><strong>Action Needed:</strong> Alerts for LCD/NCD rule updates</li>
          </ul>
        `,
        lastUpdated: new Date().toISOString()
      },
      'home': {
        screenId: 'home',
        title: 'Medical Necessity Screening System Help',
        content: `
          <h2>Welcome to the Medical Necessity Screening System</h2>
          <p>This application helps healthcare facilities determine whether medical tests meet Medicare/Medicaid coverage criteria before ordering them.</p>

          <h3>Key Features</h3>
          <ul>
            <li><strong>Real-time Screening:</strong> Check tests against LCD/NCD rules before creating EMR orders</li>
            <li><strong>Automatic ABN Generation:</strong> Create official CMS-R-131 forms for non-covered tests</li>
            <li><strong>Compliance Reporting:</strong> Track screening activity and outcomes</li>
            <li><strong>Multi-facility Support:</strong> Manage rules for multiple locations</li>
          </ul>

          <h3>Getting Started</h3>
          <ol>
            <li>Navigate to <strong>Screening</strong> to check medical necessity for patient tests</li>
            <li>Review results and generate ABNs as needed</li>
            <li>Use <strong>Reports</strong> to track screening metrics</li>
            <li>Administrators can manage system settings in <strong>Administration</strong></li>
          </ol>

          <h3>Navigation</h3>
          <ul>
            <li><strong>Screening:</strong> Main screening form for patient tests</li>
            <li><strong>Results:</strong> View screening outcomes and generate ABNs</li>
            <li><strong>ABN Preview:</strong> Create and print Advance Beneficiary Notices</li>
            <li><strong>Reports:</strong> Analytics dashboard and compliance reports</li>
            <li><strong>Administration:</strong> System configuration and user management</li>
          </ul>
        `,
        lastUpdated: new Date().toISOString()
      }
    };

    // Return the requested help content, or default to 'home' if not found
    const content = helpContent[screenId] || helpContent['home'];

    return {
      data: content
    };
  }

  /**
   * Mock Screening Result
   * This matches the structure from ScreeningService
   */
  getScreeningResult(request: any) {
    const mockResults = request.tests.map((test: any, index: number) => {
      const passed = index % 2 === 0; // Alternate pass/fail
      return {
        testId: `test-${index}`,
        cptCode: test.cptCode,
        testName: test.testName || `Test for ${test.cptCode}`,
        diagnosisCodes: test.diagnosisCodes,
        passed: passed,
        coverageDetermination: passed
          ? 'Test meets LCD/NCD criteria for medical necessity'
          : `Test does not meet LCD criteria. ICD-10 code ${test.diagnosisCodes[0]} is not covered for CPT ${test.cptCode}`,
        estimatedCharge: test.estimatedCharge || (Math.random() * 500 + 100).toFixed(2),
        requiresAbn: !passed
      };
    });

    const passedCount = mockResults.filter((r: any) => r.passed).length;
    const failedCount = mockResults.filter((r: any) => !r.passed).length;

    return {
      data: {
        screeningId: `screening-${Date.now()}`,
        timestamp: new Date().toISOString(),
        patient: request.patient,
        results: mockResults,
        summary: {
          totalTests: mockResults.length,
          passedTests: passedCount,
          failedTests: failedCount,
          totalEstimatedCharges: mockResults.reduce((sum: number, r: any) => sum + parseFloat(r.estimatedCharge), 0).toFixed(2),
          abnRequired: failedCount > 0
        }
      }
    };
  }

  /**
   * Mock LCD Files
   */
  getLcdFiles() {
    return {
      data: [
        {
          id: 'lcd-001',
          fileName: 'LCD_MolDx_NGS_Panels.xml',
          fileType: 'LCD',
          contractor: 'Palmetto GBA',
          effectiveDate: '2024-01-01',
          status: 'Active',
          uploadDate: '2024-01-15',
          uploadedBy: 'admin@test.com'
        },
        {
          id: 'lcd-002',
          fileName: 'LCD_Lab_Tests_Tumor_Markers.xml',
          fileType: 'LCD',
          contractor: 'CGS',
          effectiveDate: '2024-02-01',
          status: 'Active',
          uploadDate: '2024-02-10',
          uploadedBy: 'admin@test.com'
        }
      ]
    };
  }

  /**
   * Mock NCD Files
   */
  getNcdFiles() {
    return {
      data: [
        {
          id: 'ncd-001',
          fileName: 'NCD_Genetic_Testing.xml',
          fileType: 'NCD',
          ncdNumber: '210.2',
          effectiveDate: '2024-01-01',
          status: 'Active',
          uploadDate: '2024-01-20',
          uploadedBy: 'admin@test.com'
        }
      ]
    };
  }

  /**
   * Mock Facilities
   */
  getFacilities() {
    return {
      data: [
        {
          id: 'facility-001',
          facilityName: 'Memorial Hospital',
          facilityCode: 'MH-001',
          address: '123 Medical Center Dr',
          city: 'Boston',
          state: 'MA',
          zip: '02114',
          lcdRulesetId: 'lcd-ruleset-001',
          isActive: true
        },
        {
          id: 'facility-002',
          facilityName: 'St. Mary\'s Medical Center',
          facilityCode: 'SMMC-001',
          address: '456 Healthcare Blvd',
          city: 'Cambridge',
          state: 'MA',
          zip: '02139',
          lcdRulesetId: 'lcd-ruleset-001',
          isActive: true
        }
      ]
    };
  }

  /**
   * Mock Users
   */
  getUsers() {
    return {
      data: [
        {
          id: 'user-001',
          displayName: 'Mock User',
          email: 'mockuser@test.com',
          jobTitle: 'Medical Screener',
          role: 'Screener',
          facilityAccess: ['facility-001', 'facility-002'],
          isActive: true,
          lastLogin: new Date().toISOString()
        },
        {
          id: 'user-002',
          displayName: 'Admin User',
          email: 'admin@test.com',
          jobTitle: 'System Administrator',
          role: 'Admin',
          facilityAccess: ['facility-001', 'facility-002'],
          isActive: true,
          lastLogin: new Date().toISOString()
        }
      ]
    };
  }

  /**
   * Mock Reports Data
   */
  getReportsData() {
    return {
      data: {
        summary: {
          totalScreenings: 1250,
          passRate: 78.5,
          failRate: 21.5,
          abnsGenerated: 268,
          avgScreeningsPerDay: 45
        },
        screeningsByDay: [
          { date: '2024-01-01', total: 42, passed: 33, failed: 9 },
          { date: '2024-01-02', total: 48, passed: 38, failed: 10 },
          { date: '2024-01-03', total: 45, passed: 35, failed: 10 },
          { date: '2024-01-04', total: 50, passed: 40, failed: 10 },
          { date: '2024-01-05', total: 43, passed: 34, failed: 9 }
        ],
        topFailedTests: [
          { cptCode: '81479', testName: 'Molecular Pathology', failureCount: 45, failureRate: 32.1 },
          { cptCode: '88342', testName: 'Immunohistochemistry', failureCount: 38, failureRate: 28.4 },
          { cptCode: '84156', testName: 'Protein Analysis', failureCount: 32, failureRate: 25.8 }
        ],
        screeningsByFacility: [
          { facilityName: 'Memorial Hospital', total: 650, passed: 510, failed: 140 },
          { facilityName: 'St. Mary\'s Medical Center', total: 600, passed: 472, failed: 128 }
        ]
      }
    };
  }

  /**
   * Mock ABN Generation Response
   */
  generateAbn(data: any) {
    return {
      data: {
        abnId: `abn-${Date.now()}`,
        pdfUrl: '/mock-abn.pdf',
        generatedDate: new Date().toISOString(),
        status: 'Generated'
      }
    };
  }
}
